import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect, type ReactNode } from "react";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthProvider from "./components/AuthProvider";
import { ErrorBoundary } from "./components";
import { Toaster } from "@cointervue/design-system";
import { FullscreenLoader } from "@cointervue/design-system/components";
import { coIntervueLogo } from "@assets/Icons";

// Lazy-loaded pages
const SignInPage = lazy(() => import("./pages/signIn/SignInPage"));
const SignUpPage = lazy(() => import("./pages/SignUp/SignUpPage"));
const ForgotPassword = lazy(
  () => import("./pages/ForgetPassword/ForgetPassword"),
);
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Jobs = lazy(() => import("./pages/jobs/YourJobs"));
const Candidates = lazy(
  () => import("./pages/ShortlistedCandidates/ShortListedCandidates"),
);
const Interviews = lazy(
  () => import("./pages/ScheduledInterviews/ScheduledInterviews"),
);
const Applicants = lazy(() => import("./pages/applicants/ApplicantsPage"));
const CreateJobRole = lazy(
  () => import("./pages/createJobRoles/CreateJobRole"),
);
const EditJobRole = lazy(() => import("./pages/editJobRole/EditJobRole"));
const HelpCenter = lazy(() => import("./pages/helpCenter/HelpCenter"));

// Wrapper for suspense + error boundary
function PageWrapper({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullscreenLoader logo={coIntervueLogo} />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path !== "/jobs/create") {
      sessionStorage.removeItem("create_job_role_state");
    }
  }, [location.pathname]);

  const publicRoutes = [
    { path: "/signin", element: <SignInPage /> },
    { path: "/signup", element: <SignUpPage /> },
    { path: "/forgot-password", element: <ForgotPassword /> },
  ];

  const privateRoutes = [
    { path: "/dashboard", element: <Dashboard /> },
    { path: "/jobs", element: <Jobs /> },
    { path: "/candidates", element: <Candidates /> },
    { path: "/interviews", element: <Interviews /> },
    { path: "/jobs/:id", element: <Applicants /> },
    { path: "/jobs/create", element: <CreateJobRole /> },
    { path: "/jobs/edit/:jobId", element: <EditJobRole /> },
    { path: "/help", element: <HelpCenter /> },
  ];

  return (
    <AuthProvider>
      <Routes>
        {publicRoutes.map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={<PageWrapper>{element}</PageWrapper>}
          />
        ))}

        {privateRoutes.map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={
              <PageWrapper>
                <ProtectedRoute>{element}</ProtectedRoute>
              </PageWrapper>
            }
          />
        ))}

        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <Toaster position="top-right" richColors closeButton duration={5000} />
    </AuthProvider>
  );
}
